import { defineComponent as _defineComponent } from 'vue'
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcCheckbox from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckbox.ts.vue';
import CcCheckboxDropdown from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckboxDropdown.ts.vue';
import CcCheckBoxGroup from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckBoxGroup.ts.vue';
import CcError from '@/views/Automation/CampaignCreatorV2/components/CcError/CcError.ts.vue';
import CcRadioGroupText from '@/views/Automation/CampaignCreatorV2/components/CcRadioGroup/CcRadioGroupText.ts.vue';
import CcVariableTags from '@/views/Automation/CampaignCreatorV2/components/CcVariableTags/CcVariableTags.ts.vue';
import * as Adset from '@/views/Automation/CampaignCreatorV2/helpers/facebook/AdSet';
import useABVariableStore from '@/views/Automation/CampaignCreatorV2/store/AbVariables';
import useTargetingStore from '@/views/Automation/CampaignCreatorV2/store/Targeting';
import { SupportedEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { CardLayer } from '@/views/Automation/CampaignCreatorV2/types';
import { AdSetAbGroups } from '@/views/Automation/CampaignCreatorV2/validation/AdSet';
import { storeToRefs } from 'pinia';
import uid from 'uid';
import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Placements.ts',
  setup(__props) {

const abStore = useABVariableStore();
const targetingStore = useTargetingStore();
const targetingGetters = storeToRefs(targetingStore);
const { getField } = targetingGetters;
const placementConfigOptions = [
  {
    text: 'Advantage+ placements (recommended)',
    value: 'advantage',
    disabled: false,
    info: "Use Advantage+ placements to maximize your budget and help show your ads to more people. Facebook's delivery system will allocate your ad set's budget across multiple placements based on where they're likely to perform best.",
    id: 'ADVANTAGE_PLACEMENTS',
  },
  {
    text: 'Manual placements',
    value: 'manual',
    disabled: false,
    info: "Manually choose the places to show your ad. The more placements you select, the more opportunities you'll have to reach your target audience and achieve your business goals.",
    id: 'MANUAL_PLACEMENTS',
  },
];
const options = [
  { text: 'Facebook', id: Adset.PublisherPlatforms.facebook, value: Adset.PublisherPlatforms.facebook },
  { text: 'Instagram', id: Adset.PublisherPlatforms.instagram, value: Adset.PublisherPlatforms.instagram },
  {
    text: 'Audience Network',
    id: Adset.PublisherPlatforms.audience_network,
    value: Adset.PublisherPlatforms.audience_network,
  },
  { text: 'Messenger', id: Adset.PublisherPlatforms.messenger, value: Adset.PublisherPlatforms.messenger },
];

const deviceOptions = [
  { text: 'Mobile', value: Adset.DevicePlatforms.mobile, id: Adset.DevicePlatforms.mobile },
  { text: 'Desktop', value: Adset.DevicePlatforms.desktop, id: Adset.DevicePlatforms.desktop },
];

const devices = computed(() => {
  const devicePlatforms = targetingGetters.getField.value('device_platforms')?.value?.[0]?.value || [];
  return devicePlatforms;
});

const feedsOptions = computed(() =>
  [
    {
      text: 'Facebook Feed',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.feed}`,
      value: Adset.FacebookPositions.feed,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.desktop, Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Facebook Profile Feed',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.profile_feed}`,
      value: Adset.FacebookPositions.profile_feed,
      device: [Adset.DevicePlatforms.desktop, Adset.DevicePlatforms.mobile],
      platform: Adset.PublisherPlatforms.facebook,
    },
    {
      text: 'Instagram Feed',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.stream}`,
      value: Adset.InstagramPositions.stream,
      platform: Adset.PublisherPlatforms.instagram,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Instagram Profile Feed',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.profile_feed}`,
      value: Adset.InstagramPositions.profile_feed,
      platform: Adset.PublisherPlatforms.instagram,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Facebook Marketplace',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.marketplace}`,
      value: Adset.FacebookPositions.marketplace,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile, Adset.DevicePlatforms.desktop],
    },
    {
      text: 'Facebook Video Feed',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.video_feeds}`,
      value: Adset.FacebookPositions.video_feeds,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile, Adset.DevicePlatforms.desktop],
    },
    {
      text: 'Facebook Right Column',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.right_hand_column}`,
      value: Adset.FacebookPositions.right_hand_column,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.desktop],
    },
    {
      text: 'Instagram Explore',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.explore}`,
      value: Adset.InstagramPositions.explore,
      platform: Adset.PublisherPlatforms.instagram,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Messenger Inbox',
      id: `${Adset.PublisherPlatforms.messenger}-${Adset.MessengerPositions.messenger_home}`,
      value: Adset.MessengerPositions.messenger_home,
      platform: Adset.PublisherPlatforms.messenger,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Instagram Explore Home',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.explore_home}`,
      value: Adset.InstagramPositions.explore_home,
      platform: Adset.PublisherPlatforms.instagram,
      disabled: true,
      device: [Adset.DevicePlatforms.mobile],
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || (!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

const storiesOptions = computed((d) =>
  [
    {
      text: 'Facebook Stories',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.story}`,
      platform: Adset.PublisherPlatforms.facebook,
      value: Adset.FacebookPositions.story,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Instagram Stories',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.story}`,
      platform: Adset.PublisherPlatforms.instagram,
      value: Adset.InstagramPositions.story,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Messenger Stories',
      id: `${Adset.PublisherPlatforms.messenger}-${Adset.MessengerPositions.story}`,
      value: Adset.MessengerPositions.story,
      platform: Adset.PublisherPlatforms.messenger,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Instagram Reels',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.reels}`,
      value: Adset.InstagramPositions.reels,
      platform: Adset.PublisherPlatforms.instagram,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Facebook Reels',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.facebook_reels}`,
      value: Adset.FacebookPositions.facebook_reels,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile],
      disabled: false,
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || (!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

const inStreamOptions = computed(() =>
  [
    {
      text: 'Facebook In-Stream Videos',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.instream_video}`,
      value: Adset.FacebookPositions.instream_video,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile, Adset.DevicePlatforms.desktop],
    },
    {
      text: 'Ads on Facebook Reels',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.facebook_reels_overlay}`,
      value: Adset.FacebookPositions.facebook_reels_overlay,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Ads on Instagram Reels',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.reels_overlay}`,
      value: Adset.InstagramPositions.reels_overlay,
      platform: Adset.PublisherPlatforms.instagram,
      disabled: false,
      device: [Adset.DevicePlatforms.mobile],
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || (!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

const searchResultsOptions = computed(() =>
  [
    {
      text: 'Facebook search result',
      id: `${Adset.PublisherPlatforms.facebook}-${Adset.FacebookPositions.search}`,
      value: Adset.FacebookPositions.search,
      platform: Adset.PublisherPlatforms.facebook,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Instagram search result',
      id: `${Adset.PublisherPlatforms.instagram}-${Adset.InstagramPositions.ig_search}`,
      value: Adset.InstagramPositions.ig_search,
      platform: Adset.PublisherPlatforms.instagram,
      device: [Adset.DevicePlatforms.mobile],
      disabled: true,
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || (!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

const messagesOptions = computed(() =>
  [
    {
      text: 'Messenger sponsored messages',
      id: `${Adset.PublisherPlatforms.messenger}-${Adset.MessengerPositions.sponsored_messages}`,
      value: Adset.MessengerPositions.sponsored_messages,
      platform: Adset.PublisherPlatforms.messenger,
      device: [Adset.DevicePlatforms.mobile],
      disabled: true,
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || (!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

const appsOptions = computed(() =>
  [
    {
      text: 'Audience Network native, banner and interstitial',
      id: `${Adset.PublisherPlatforms.audience_network}-${Adset.AudienceNetworkPlacements.classic}`,
      value: Adset.AudienceNetworkPlacements.classic,
      platform: Adset.PublisherPlatforms.audience_network,
      device: [Adset.DevicePlatforms.mobile],
    },
    {
      text: 'Audience Network rewarded videos',
      id: `${Adset.PublisherPlatforms.audience_network}-${Adset.AudienceNetworkPlacements.rewarded_video}`,
      value: Adset.AudienceNetworkPlacements.rewarded_video,
      platform: Adset.PublisherPlatforms.audience_network,
      device: [Adset.DevicePlatforms.mobile],
      disabled: false,
    },
  ].map((e) => ({
    ...e,
    active: e.disabled || !!(!devices.value.some((d) => e.device.includes(d)) && devices.value.length),
  }))
);

// Placements
const allItemsSelected = computed(() => {
  const audienceNetwork = (targetingGetters.getField.value('audience_network_positions')?.value?.[0]?.value || []).map(
    (e) => `${Adset.PublisherPlatforms.audience_network}-${e}`
  );
  const instagram = (targetingGetters.getField.value('instagram_positions')?.value?.[0]?.value || []).map(
    (e) => `${Adset.PublisherPlatforms.instagram}-${e}`
  );
  const facebook = (targetingGetters.getField.value('facebook_positions')?.value?.[0]?.value || []).map(
    (e) => `${Adset.PublisherPlatforms.facebook}-${e}`
  );
  const messenger = (targetingGetters.getField.value('messenger_positions')?.value?.[0]?.value || []).map(
    (e) => `${Adset.PublisherPlatforms.messenger}-${e}`
  );
  return [...audienceNetwork, ...instagram, ...facebook, ...messenger];
});

const advantageAudienceMode = computed(() => {
  const targetingOptimization = targetingGetters.getField.value('targeting_automation')?.value?.[0] || {};
  return { ...targetingOptimization, value: targetingOptimization?.value?.advantage_audience ? 'advantage' : 'manual' };
});

const publisherPlatforms = computed(() => {
  const publisherPlatforms = targetingGetters.getField.value('publisher_platforms')?.value || [];
  return publisherPlatforms?.[0]?.value ?? []; // TODO check if index 0 is correct
});

const appsSelected = computed(() =>
  allItemsSelected.value.filter((o) =>
    appsOptions.value.some((option) => option.id === o && devices.value.some((d) => option.device?.includes(d)))
  )
);

const messagesSelected = computed(() =>
  allItemsSelected.value.filter((o) =>
    messagesOptions.value.some((option) => option.id === o && devices.value.some((d) => option.device?.includes(d)))
  )
);

const searchResultsSelected = computed(() =>
  allItemsSelected.value.filter((e) =>
    searchResultsOptions.value.some(
      (option) => option.id === e && devices.value.some((d) => option.device?.includes(d))
    )
  )
);

const inStreamSelected = computed(() =>
  allItemsSelected.value.filter((e) =>
    inStreamOptions.value.some((option) => option.id === e && devices.value.some((d) => option.device?.includes(d)))
  )
);

const storiesSelected = computed(() =>
  allItemsSelected.value.filter((e) =>
    storiesOptions.value.some((option) => option.id === e && devices.value.some((d) => option.device?.includes(d)))
  )
);

const feedsSelected = computed(() => {
  const value = allItemsSelected.value.filter((e) =>
    feedsOptions.value.some((option) => option.id === e && devices.value.some((d) => option.device?.includes(d)))
  );
  return value;
});

const selectedText = computed(() => {
  const allOptions = [
    ...feedsOptions.value,
    ...storiesOptions.value,
    ...inStreamOptions.value,
    ...searchResultsOptions.value,
    ...messagesOptions.value,
    ...appsOptions.value,
  ];
  const allSelectedItems = allItemsSelected.value;
  return `${allSelectedItems.length} / ${allOptions.length} placements that support asset customization`;
});

const indeterminatePlatform = computed(() => {
  const allOptions = [
    ...feedsOptions.value,
    ...storiesOptions.value,
    ...inStreamOptions.value,
    ...searchResultsOptions.value,
    ...messagesOptions.value,
    ...appsOptions.value,
  ];
  const selectedPlatforms = allOptions.filter((e) => allItemsSelected.value.includes(e.id));
  const uniquePlatforms = [...new Set(selectedPlatforms.map((e) => e.platform))];
  return uniquePlatforms;
});
function filterOptions(
  option: { value?: string; device?: string[]; platform?: string; id: string },
  device?: string[],
  platform?: string[],
  stories?: string[],
  currentSelected: string[] = []
) {
  if (device) {
    return option.device?.some((e) => device.includes(e));
  }
  if (platform?.length) {
    return platform.includes(option.platform || 'none');
  }
  if (stories) {
    return stories.includes(option.id || 'none');
  }
  return currentSelected.includes(option.id || 'none');
}

function updateState(
  placements: Pick<
    Adset.TargetingObjectAbVariables,
    | 'device_platforms'
    | 'facebook_positions'
    | 'instagram_positions'
    | 'audience_network_positions'
    | 'messenger_positions'
    | 'publisher_platforms'
  >
) {
  const { variableId, abGroup } = targetingGetters.getField.value('instagram_positions')?.value?.[0] || {};
  targetingStore.addOrEditVariable('instagram_positions', {
    field: 'instagram_positions',
    value: placements.instagram_positions,
    variableId,
    abGroup,
  });
  const { variableId: facebookVariableId, abGroup: facebookAbGroup } =
    targetingGetters.getField.value('facebook_positions')?.value?.[0] || {};
  targetingStore.addOrEditVariable('facebook_positions', {
    field: 'facebook_positions',
    value: placements.facebook_positions,
    variableId: facebookVariableId,
    abGroup: facebookAbGroup,
  });
  const { variableId: audienceNetworkVariableId, abGroup: audienceNetworkAbGroup } =
    targetingGetters.getField.value('audience_network_positions')?.value?.[0] || {};
  targetingStore.addOrEditVariable('audience_network_positions', {
    field: 'audience_network_positions',
    value: placements.audience_network_positions,
    variableId: audienceNetworkVariableId,
    abGroup: audienceNetworkAbGroup,
  });

  const { variableId: messengerVariableId, abGroup: messengerAbGroup } =
    targetingGetters.getField.value('messenger_positions')?.value?.[0] || {};

  targetingStore.addOrEditVariable('messenger_positions', {
    field: 'messenger_positions',
    value: placements.messenger_positions,
    variableId: messengerVariableId,
    abGroup: messengerAbGroup,
  });
  const { variableId: deviceVariableId, abGroup: deviceAbGroup } =
    targetingGetters.getField.value('device_platforms')?.value?.[0] || {};
  targetingStore.addOrEditVariable('device_platforms', {
    field: 'device_platforms',
    value: placements.device_platforms,
    variableId: deviceVariableId,
    abGroup: deviceAbGroup,
  });
  const { variableId: publisherVariableId, abGroup: publisherAbGroup } =
    targetingGetters.getField.value('publisher_platforms')?.value?.[0] || {};
  targetingStore.addOrEditVariable('publisher_platforms', {
    field: 'publisher_platforms',
    value: placements.publisher_platforms,
    variableId: publisherVariableId,
    abGroup: publisherAbGroup,
  });
}

function select(filters: {
  device?: string[];
  platform?: string[];
  feed?: string[];
  stories?: string[];
  inStream?: string[];
  search?: string[];
  messages?: string[];
  apps?: string[];
}) {
  const { device, platform, feed, stories, inStream, search, messages, apps } = filters;
  if (device && !device.length) {
    return;
  }
  // FEEDS
  const feedsSelectedNew = feedsOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, feed, feedsSelected.value));

  // STORIES
  const storiesSelectedNew = storiesOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, stories, storiesSelected.value));

  // IN-STREAM
  const instreamSelectedNew = inStreamOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, inStream, inStreamSelected.value));

  // SEARCH RESULTS
  const selectedSearchNew = searchResultsOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, search, searchResultsSelected.value));

  // MESSAGES RESULTS
  const selectedMessagesNew = messagesOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, messages, messagesSelected.value));

  // APPS
  const sleetedAppsNew = appsOptions.value
    .filter((e) => (devices.value.length ? !e.disabled : !e.active))
    .filter((o) => filterOptions(o, device, platform, apps, appsSelected.value));

  const allSelectedItems = [
    ...feedsSelectedNew,
    ...storiesSelectedNew,
    ...instreamSelectedNew,
    ...selectedSearchNew,
    ...selectedMessagesNew,
    ...sleetedAppsNew,
  ];

  const payload = {
    publisher_platforms: [...new Set(allSelectedItems.map((e) => e.platform))] as unknown as Adset.PublisherPlatforms[],
    facebook_positions: allSelectedItems
      .filter((e) => e.platform === Adset.PublisherPlatforms.facebook)
      .map((e) => e.value),
    instagram_positions: allSelectedItems
      .filter((e) => e.platform === Adset.PublisherPlatforms.instagram)
      .map((e) => e.value),
    messenger_positions: allSelectedItems
      .filter((e) => e.platform === Adset.PublisherPlatforms.messenger)
      .map((e) => e.value),
    audience_network_positions: allSelectedItems
      .filter((e) => e.platform === Adset.PublisherPlatforms.audience_network)
      .map((e) => e.value),
    device_platforms: device?.length ? device : (devices.value as unknown as Adset.DevicePlatforms[]),
  };
  updateState(payload as any);
}

function singleSelected(value: {
  feed?: string[];
  stories?: string[];
  inStream?: string[];
  search?: string[];
  messages?: string[];
  apps?: string[];
}) {
  select(value);
}

function onPlatformChange(selected: any[]) {
  select({ platform: selected });
}

function selectAll(value: boolean) {
  if (value) {
    select({ device: [Adset.DevicePlatforms.desktop, Adset.DevicePlatforms.mobile] });
  } else {
    select({ device: ['none'] });
  }
}

function updatePlacementAutomation(value: string, variableId?: string) {
  const optimization = {
    advantage_audience: value === 'advantage' ? 1 : 0,
  };
  targetingStore.addOrEditVariable('targeting_automation', {
    field: 'targeting_automation',
    value: optimization,
    variableId: variableId!,
  });
  if (value === 'advantage') {
    abStore.resetField(SupportedEntities.targeting, 'publisher_platforms');
    abStore.resetField(SupportedEntities.targeting, 'device_platforms');
    abStore.resetField(SupportedEntities.targeting, 'facebook_positions');
    abStore.resetField(SupportedEntities.targeting, 'instagram_positions');
    abStore.resetField(SupportedEntities.targeting, 'audience_network_positions');
    abStore.resetField(SupportedEntities.targeting, 'messenger_positions');
  }
  if (value !== 'advantage') {
    selectAll(true);
  }
}

function onNewGroupAdd() {
  targetingStore.addOrEditVariable('targeting_automation', {
    field: 'targeting_automation',
    value: { advantage_audience: 1 },
    variableId: uid(),
  });
}

function onDeviceChange(device: string[]) {
  select({ device });
}

return { __sfc: true,abStore, targetingStore, targetingGetters, getField, placementConfigOptions, options, deviceOptions, devices, feedsOptions, storiesOptions, inStreamOptions, searchResultsOptions, messagesOptions, appsOptions, allItemsSelected, advantageAudienceMode, publisherPlatforms, appsSelected, messagesSelected, searchResultsSelected, inStreamSelected, storiesSelected, feedsSelected, selectedText, indeterminatePlatform, filterOptions, updateState, select, singleSelected, onPlatformChange, selectAll, updatePlacementAutomation, onNewGroupAdd, onDeviceChange, CcCard, CcCardItem, CcCheckbox, CcCheckboxDropdown, CcCheckBoxGroup, CcError, CcRadioGroupText, CcVariableTags, SupportedEntities, CardLayer, AdSetAbGroups }
}

})